import { Injectable } from '@angular/core';
import { Chart } from 'chart.js';
import { ErrorPanelService } from './error-panel.service';
import { GraphTopic, NUCLEAR_TOPICS_EXPLANATION } from '../models/ai-model';
import { AiGraphicsComponent } from '../sections/ai-graphics/ai-graphics.component';
import { AI_DISCLAIMER_COPY } from '../models/descriptions/ai-disclaimer';
import { NuclearTopicsLabelService } from './label.nucleartopics.service';
import {
  buildNuclearTopicPdfRows,
  renderNuclearTopicsPdf,
} from './nuclear-topics-pdf';

interface DownloadPDFOptions {
  filename: string;
  isGraphics: boolean;
  isSummary: boolean;
  element: HTMLElement;
  graphicsComponent?: AiGraphicsComponent;
}

interface LegendItem {
  category: string;
  color: string;
  label: string;
}

const PDF_HEADING_STYLES: Record<
  string,
  { fontSize: number; lineHeight: number }
> = {
  H1: { fontSize: 16, lineHeight: 0.28 },
  H2: { fontSize: 14, lineHeight: 0.24 },
  H3: { fontSize: 12, lineHeight: 0.22 },
};

const PDF_BODY_TEXT = { fontSize: 11, lineHeight: 0.19, bold: false };

@Injectable({
  providedIn: 'root',
})
export class PdfExportService {
  constructor(
    private errorPanelService: ErrorPanelService,
    private nuclearTopicsLabelService: NuclearTopicsLabelService
  ) {}

  async exportStandardPDF(element: HTMLElement, filename: string) {
    const exportTarget = element.querySelector<HTMLElement>('.container-text');
    const htmlContent = exportTarget
      ? this.extractHtmlContent(exportTarget)
      : null;

    if (!htmlContent) {
      this.errorPanelService.showErrorSnackBar(
        'Nessun contenuto disponibile per il PDF'
      );
      return;
    }

    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter',
      });

      const margin = 0.5;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      this.writeHtmlContentToPdf(
        pdf,
        htmlContent,
        margin,
        pageWidth,
        pageHeight
      );
      this.applyAiExportMarking(pdf, {
        title: filename,
        margin,
        pageWidth,
        pageHeight,
      });
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF file', error);
      this.errorPanelService.showErrorSnackBar('Errore nel generare il PDF');
    }
  }

  private extractHtmlContent(container: HTMLElement): string | null {
    const contentDiv = container.querySelector<HTMLElement>(
      'div:not(.markdown-editor):not(.no-summary):not(.loading-container)'
    );
    return contentDiv?.innerHTML.trim() || null;
  }

  applyAiExportMarking(
    pdf: import('jspdf').jsPDF,
    options: {
      title?: string;
      margin?: number;
      pageWidth?: number;
      pageHeight?: number;
    } = {}
  ): void {
    this.addAiGeneratedMetadata(pdf, options.title);
    this.addAiDisclaimerFooter(
      pdf,
      options.margin,
      options.pageWidth,
      options.pageHeight
    );
  }

  private addAiGeneratedMetadata(
    pdf: import('jspdf').jsPDF,
    title?: string
  ): void {
    pdf.setProperties({
      title: title ?? '',
      keywords: AI_DISCLAIMER_COPY.metadataKeyword,
    });
  }

  private addAiDisclaimerFooter(
    pdf: import('jspdf').jsPDF,
    margin: number = 0.5,
    pageWidth: number = pdf.internal.pageSize.getWidth(),
    pageHeight: number = pdf.internal.pageSize.getHeight()
  ): void {
    const lines = pdf.splitTextToSize(
      AI_DISCLAIMER_COPY.pdfNotice,
      pageWidth - margin * 2
    );
    const lineHeight = 0.12;
    const footerY =
      pageHeight - 0.28 - Math.max(0, lines.length - 1) * lineHeight;

    for (let page = 1; page <= pdf.getNumberOfPages(); page++) {
      pdf.setPage(page);
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(8);
      pdf.setTextColor(130, 130, 130);
      pdf.text(lines, margin, footerY);
    }
    pdf.setTextColor(0, 0, 0);
  }

  private writeHtmlContentToPdf(
    pdf: import('jspdf').jsPDF,
    html: string,
    margin: number,
    pageWidth: number,
    pageHeight: number,
    startY: number = margin
  ): void {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html.trim();

    const contentRoot =
      wrapper.children.length === 1 &&
      wrapper.firstElementChild?.tagName === 'DIV'
        ? (wrapper.firstElementChild as HTMLElement)
        : wrapper;

    let y = startY;
    const contentWidth = pageWidth - margin * 2;

    for (const block of Array.from(contentRoot.children) as HTMLElement[]) {
      y = this.writeHtmlBlockToPdf(
        pdf,
        block,
        margin,
        y,
        contentWidth,
        pageHeight,
        margin
      );
    }
  }

  private writeHtmlBlockToPdf(
    pdf: import('jspdf').jsPDF,
    block: HTMLElement,
    x: number,
    y: number,
    contentWidth: number,
    pageHeight: number,
    pageMargin: number
  ): number {
    const tag = block.tagName;
    const headingStyle = PDF_HEADING_STYLES[tag];

    if (headingStyle) {
      return this.writeTextBlock(
        pdf,
        block.textContent ?? '',
        x,
        y,
        contentWidth,
        pageHeight,
        pageMargin,
        headingStyle.fontSize,
        true,
        headingStyle.lineHeight
      );
    }

    if (tag === 'P') {
      return this.writeRichParagraph(
        pdf,
        block,
        x,
        y,
        contentWidth,
        pageHeight,
        pageMargin
      );
    }

    if (tag === 'UL' || tag === 'OL') {
      let currentY = y;
      const listIndent = 0.15;

      block.querySelectorAll('li').forEach((item, index) => {
        const prefix = tag === 'OL' ? `${index + 1}. ` : '• ';
        currentY = this.writeTextBlock(
          pdf,
          `${prefix}${item.textContent ?? ''}`,
          x + listIndent,
          currentY,
          contentWidth - listIndent,
          pageHeight,
          pageMargin,
          PDF_BODY_TEXT.fontSize,
          PDF_BODY_TEXT.bold,
          PDF_BODY_TEXT.lineHeight
        );
      });

      return currentY + 0.08;
    }

    if (block.textContent?.trim()) {
      return this.writeTextBlock(
        pdf,
        block.textContent,
        x,
        y,
        contentWidth,
        pageHeight,
        pageMargin,
        PDF_BODY_TEXT.fontSize,
        PDF_BODY_TEXT.bold,
        PDF_BODY_TEXT.lineHeight
      );
    }

    return y;
  }

  /** Scrive un paragrafo preservando il grassetto su strong/b. */
  private writeRichParagraph(
    pdf: import('jspdf').jsPDF,
    paragraph: HTMLElement,
    x: number,
    y: number,
    contentWidth: number,
    pageHeight: number,
    pageMargin: number
  ): number {
    const segments = this.getTextSegments(paragraph);
    if (!segments.length) {
      return y;
    }

    const fontSize = 11;
    const lineHeight = 0.19;
    let currentY = y;
    let currentX = x;
    const maxX = x + contentWidth;

    const startNewPage = () => {
      pdf.addPage();
      currentY = pageMargin;
      currentX = x;
    };

    const ensureLineSpace = () => {
      if (currentY + lineHeight > pageHeight - pageMargin) {
        startNewPage();
      }
    };

    ensureLineSpace();

    for (const segment of segments) {
      pdf.setFont('helvetica', segment.bold ? 'bold' : 'normal');
      pdf.setFontSize(fontSize);

      const words = segment.text.split(/(\s+)/).filter((part) => part.length);

      for (const word of words) {
        const wordWidth = pdf.getTextWidth(word);

        if (currentX + wordWidth > maxX && currentX > x) {
          currentY += lineHeight;
          currentX = x;
          ensureLineSpace();
        }

        pdf.text(word, currentX, currentY);
        currentX += wordWidth;
      }
    }

    return currentY + lineHeight + 0.08;
  }

  private getTextSegments(
    element: HTMLElement
  ): { text: string; bold: boolean }[] {
    const segments: { text: string; bold: boolean }[] = [];

    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text) {
          segments.push({ text, bold: false });
        }
        return;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const bold = ['STRONG', 'B'].includes(el.tagName);
        const text = el.textContent ?? '';
        if (text) {
          segments.push({ text, bold });
        }
      }
    });

    if (!segments.length && element.textContent) {
      segments.push({ text: element.textContent, bold: false });
    }

    return segments;
  }

  private writeTextBlock(
    pdf: import('jspdf').jsPDF,
    text: string,
    x: number,
    y: number,
    contentWidth: number,
    pageHeight: number,
    pageMargin: number,
    fontSize: number,
    bold: boolean,
    lineHeight: number
  ): number {
    const normalizedText = text.replace(/\s+/g, ' ').trim();
    if (!normalizedText) {
      return y;
    }

    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(fontSize);

    const lines = pdf.splitTextToSize(normalizedText, contentWidth);
    let currentY = y;

    for (const line of lines) {
      if (currentY + lineHeight > pageHeight - pageMargin) {
        pdf.addPage();
        currentY = pageMargin;
      }
      pdf.text(line, x, currentY);
      currentY += lineHeight;
    }

    return currentY + 0.08;
  }

  /**
   * PDF del singolo colloquio: grafico a barre a tutta pagina, descrizioni dalla pagina successiva.
   * Il grafico è disegnato in vettoriale, così le etichette restano orizzontali e leggibili.
   */
  async exportNuclearTopicsPdf(options: {
    filename: string;
    distribution: Record<string, number> | null;
    subtitle?: string;
  }) {
    const distribution = options.distribution;
    if (!distribution || Object.keys(distribution).length === 0) {
      this.errorPanelService.showErrorSnackBar(
        'Distribuzione dei topic nucleari non disponibile'
      );
      return;
    }

    try {
      const rows = buildNuclearTopicPdfRows(
        distribution,
        this.nuclearTopicsLabelService.getTopicLabels(),
        NUCLEAR_TOPICS_EXPLANATION
      );
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter',
      });

      renderNuclearTopicsPdf(pdf, {
        rows,
        subtitle: options.subtitle,
      });
      this.applyAiExportMarking(pdf, {
        title: options.filename,
        margin: 0.58,
      });
      pdf.save(options.filename);
    } catch (error) {
      console.error('Error in exportNuclearTopicsPdf:', error);
      this.errorPanelService.showErrorSnackBar('Errore nel generare il PDF');
    }
  }

  /**
   * Esporta il grafico topic, la legenda e le descrizioni con jsPDF.
   * Il canvas visibile viene convertito in immagine (`toBase64Image`) e inserito
   * direttamente nel PDF, senza html2canvas che in questo contesto produce pagine bianche.
   */
  async exportGraphicsPDF(
    filename: string,
    chart: Chart,
    legendItems: LegendItem[],
    topicExplanations: GraphTopic[]
  ) {
    try {
      const chartImage = chart.toBase64Image('image/png', 2);
      const imageSize = await this.getImageDimensions(chartImage);
      const { jsPDF } = await import('jspdf');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: 'letter',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 0.5;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text('Topic Nucleari nel Tempo', pageWidth / 2, y + 0.25, {
        align: 'center',
      });
      y += 0.55;

      const chartHeight = (imageSize.height / imageSize.width) * contentWidth;
      const legendSpace = 1.2;
      const maxChartHeight = pageHeight - margin - y - legendSpace;
      const finalChartHeight = Math.min(chartHeight, maxChartHeight);
      const finalChartWidth =
        chartHeight > maxChartHeight
          ? (imageSize.width / imageSize.height) * finalChartHeight
          : contentWidth;
      const chartX = margin + (contentWidth - finalChartWidth) / 2;

      pdf.addImage(
        chartImage,
        'PNG',
        chartX,
        y,
        finalChartWidth,
        finalChartHeight
      );
      y += finalChartHeight + 0.25;

      this.addLegendToPdf(
        pdf,
        legendItems,
        margin,
        y,
        contentWidth,
        pageHeight
      );

      this.addTopicDescriptionsToPdf(
        pdf,
        topicExplanations,
        margin,
        pageWidth,
        pageHeight
      );

      this.applyAiExportMarking(pdf, {
        title: filename,
        margin,
        pageWidth,
        pageHeight,
      });
      pdf.save(filename);
    } catch (error) {
      console.error('Error in exportGraphicsPDF:', error);
      this.errorPanelService.showErrorSnackBar('Errore nel generare il PDF');
    }
  }

  private getImageDimensions(
    dataUrl: string
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /** Disegna i chip della legenda con i colori delle categorie. */
  private addLegendToPdf(
    pdf: import('jspdf').jsPDF,
    legendItems: LegendItem[],
    margin: number,
    startY: number,
    contentWidth: number,
    pageHeight: number
  ): number {
    const chipHeight = 0.28;
    const chipPaddingX = 0.12;
    const chipGap = 0.1;
    let y = startY;
    let x = margin;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);

    for (const item of legendItems) {
      const textWidth = pdf.getTextWidth(item.label);
      const chipWidth = textWidth + chipPaddingX * 2;

      if (x + chipWidth > margin + contentWidth) {
        x = margin;
        y += chipHeight + chipGap;
      }

      if (y + chipHeight > pageHeight - margin) {
        pdf.addPage();
        y = margin;
        x = margin;
      }

      const { r, g, b } = this.parseRgbColor(item.color);
      pdf.setFillColor(r, g, b);
      pdf.roundedRect(x, y, chipWidth, chipHeight, 0.08, 0.08, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.text(item.label, x + chipPaddingX, y + chipHeight * 0.72);
      pdf.setTextColor(0, 0, 0);

      x += chipWidth + chipGap;
    }

    return y + chipHeight;
  }

  /** Aggiunge le descrizioni dei topic nucleari su una o più pagine. */
  private addTopicDescriptionsToPdf(
    pdf: import('jspdf').jsPDF,
    topicExplanations: GraphTopic[],
    margin: number,
    pageWidth: number,
    pageHeight: number
  ): void {
    pdf.addPage();

    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('Descrizione Topic Nucleari', pageWidth / 2, y + 0.25, {
      align: 'center',
    });
    y += 0.6;

    for (const topic of topicExplanations) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      const titleLines = pdf.splitTextToSize(topic.topic, contentWidth);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const descriptionLines = pdf.splitTextToSize(
        topic.description,
        contentWidth
      );

      const blockHeight =
        titleLines.length * 0.18 + descriptionLines.length * 0.15 + 0.25;

      if (y + blockHeight > pageHeight - margin) {
        pdf.addPage();
        y = margin;
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(51, 51, 51);
      pdf.text(titleLines, margin, y);
      y += titleLines.length * 0.18 + 0.08;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(102, 102, 102);
      pdf.text(descriptionLines, margin, y);
      pdf.setTextColor(0, 0, 0);
      y += descriptionLines.length * 0.15 + 0.2;
    }
  }

  private parseRgbColor(color: string): { r: number; g: number; b: number } {
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      return { r: +match[1], g: +match[2], b: +match[3] };
    }
    return { r: 112, g: 128, b: 144 };
  }

  async downloadPDF(options: DownloadPDFOptions) {
    const { filename, isGraphics, isSummary, element, graphicsComponent } =
      options;

    const fullFilename =
      filename +
      (isGraphics
        ? ' - Grafico Topic'
        : isSummary
          ? ' - Sintesi Complessiva'
          : ' - Scheda Clinica Dinamica');

    if (isGraphics && graphicsComponent?.getChart()) {
      const legendItems = graphicsComponent.categories
        .map((category, index) => ({
          category: category.name,
          color: graphicsComponent.getColors()[index],
          label: graphicsComponent.nuclearTopicsLabels[category.name],
          hidden: category.hidden,
        }))
        .filter((item) => !item.hidden);

      await this.exportGraphicsPDF(
        fullFilename,
        graphicsComponent.getChart()!,
        legendItems,
        graphicsComponent.nuclearTopicsExplanation
      );
      return;
    }

    if (element) {
      await this.exportStandardPDF(element, fullFilename);
    }
  }
}
